import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../../../styles/Form.css";
import { MODE } from "../../../constants/SiteConstants";

const CustomProductForm = ({
  fields,
  onSubmit,
  mode,
  initialData = {},
  handleVideoChange,
  videoPreview,
  onFieldChange,
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [dropdownFields, setDropdownFields] = useState([]);

  useEffect(() => {
    if (initialData && mode === MODE.UPDATE) {
      const formattedData = { ...initialData };

      // Ensure the date fields are formatted correctly for the input
      if (formattedData.date) {
        formattedData.date = new Date(formattedData.date).toISOString().split("T")[0]; // Format date to YYYY-MM-DD
      }
      if (formattedData.time) {
        formattedData.time = formattedData.time; // Keep time as is
      }

      setFormData(formattedData);
      setDropdownFields(initialData.options || []);
    }
  }, [initialData, mode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: undefined,
      }));
    }

    if (onFieldChange) {
      onFieldChange(name, type === "checkbox" ? checked : value);
    }
  };

  const validateForm = () => {
    const formErrors = {};

    fields.forEach((field) => {
      const fieldValue = formData[field.name];

      if (field.required && !fieldValue) {
        formErrors[field.name] = `${field.label} is required`;
      }
    });

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    if (validateForm()) {
      const finalFormData = {
        ...formData,
        options: dropdownFields,
      };

      // Format date for submission
      if (finalFormData.date) {
        finalFormData.date = new Date(finalFormData.date).toISOString(); // Convert to ISO string
      }
      
      onSubmit(finalFormData);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {fields.map((field) => (
        <div className="mb-4" key={field.name}>
          <label
            htmlFor={field.name}
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {field.label}
          </label>

          {field.type === "file" && field.accept === "video/*" ? (
            <>
              <input
                type="file"
                className="w-full rounded-md border border-stroke p-3 outline-none file:mr-4 file:rounded file:border-[0.5px] file:border-stroke file:bg-[#EEEEEE] file:py-1 file:px-2.5 file:text-sm"
                id={field.name}
                name={field.name}
                onChange={handleVideoChange}
                accept={field.accept}
              />
              {videoPreview && (
                <video className="mt-2 w-full rounded-lg" controls>
                  <source src={videoPreview} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </>
          ) : field.type === "rich-text" ? (
            <ReactQuill
              value={formData[field.name] || ""}
              onChange={(value) =>
                handleChange({ target: { name: field.name, value } })
              }
              placeholder={field.placeholder}
              className="mt-2"
            />
          ) : field.type === "date" ? (
            <input
              type="date"
              className="block w-full border border-gray-300 rounded-lg py-2 px-3 text-gray-700 focus:border-blue-500 focus:ring-blue-500 focus:outline-none"
              id={field.name}
              name={field.name}
              value={formData[field.name] || ""}
              onChange={handleChange}
            />
          ) : field.type === "time" ? (
            <input
              type="time"
              className="block w-full border border-gray-300 rounded-lg py-2 px-3 text-gray-700 focus:border-blue-500 focus:ring-blue-500 focus:outline-none"
              id={field.name}
              name={field.name}
              value={formData[field.name] || ""}
              onChange={handleChange}
            />
          ) : (
            <input
              type={field.type || "text"}
              className="block w-full border border-gray-300 rounded-lg py-2 px-3 text-gray-700 focus:border-blue-500 focus:ring-blue-500 focus:outline-none"
              id={field.name}
              name={field.name}
              placeholder={field.placeholder}
              value={formData[field.name] || ""}
              onChange={handleChange}
            />
          )}

          {errors[field.name] && (
            <p className="text-red-500 text-sm mt-1">{errors[field.name]}</p>
          )}
        </div>
      ))}

      <button
        type="submit"
        className="mt-4 w-full rounded-lg bg-[#1C2434] text-white py-3 hover:bg-[#1C2434]"
      >
        {mode === MODE.ADD ? "Add" : "Update"} Data
      </button>
    </form>
  );
};

export default CustomProductForm;
